import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { z } from 'zod';

import Button from '../../common/Button';
import { toastPromise } from '../../common/utils';
import { useGetProjectById } from '../../hooks';
import { useGetDatasets } from '../../hooks/tanstack/useDatasetsHook';
import {
  useGetModelOutput,
  useUpdateModel,
} from '../../hooks/tanstack/useModelsHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import FormInput from '../form/FormInput';
import FormSelect from '../form/FormSelect';

const DatasetConfigurationSchema = z.object({
  label: z.string(),
  value: z.string(),
});

type DatasetConfigurationSchemaType = z.infer<
  typeof DatasetConfigurationSchema
>;

const DatasetConfiguration = () => {
  const form = useForm<DatasetConfigurationSchemaType>({
    resolver: zodResolver(DatasetConfigurationSchema),
    defaultValues: {
      label: '',
      value: '',
    },
  });

  const { handleSubmit, reset, watch, setValue } = form;

  const [openSelectDatasetDialog, setOpenSelectDatasetDialog] = useState(false);

  const { projectId, orgId, modelId } = useGetRouteParams();

  const { model, isDefault, isRunningModelOutputServer } =
    useGetDistinguishModel();

  const { data: datasets } = useGetDatasets(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const { data: project } = useGetProjectById(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const { refetch: refetchModelOutput } = useGetModelOutput(
    {
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!orgId && !!projectId && !!modelId,
    }
  );

  const { mutateAsync: updateModel, isPending: isUpdatingModel } =
    useUpdateModel({
      onSuccess: () => {
        refetchModelOutput();
        handleCloseDatasetDialog();
      },
    });

  useEffect(() => {
    if (model) {
      reset({
        label: model.dataset.name,
        value: model.dataset.id.toString(),
      });
    }
  }, [model]);

  const handleCloseDatasetDialog = () => {
    setOpenSelectDatasetDialog(false);
  };

  const handleOpenDatasetDialog = () => {
    setOpenSelectDatasetDialog(true);
  };

  const handleOnSubmit = async () => {
    const { value } = watch();

    const selectedDataset = datasets?.find((d) => d.id === Number(value));

    if (!selectedDataset) {
      return;
    }

    const res = updateModel({
      projectId,
      orgId,
      modelId,
      data: {
        dataset: Number(value),
      },
    });

    const resData = await res;

    toastPromise({
      promise: res,
      content: "Model's dataset updated successfully",
    });

    const newDataset = datasets?.find((d) => d.id === Number(resData.dataset));

    if (newDataset) {
      setValue('label', newDataset.name);
    }

    handleCloseDatasetDialog();
  };

  const isDisabled = project?.is_guest_project;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">Dataset</h1>
            {!model?.is_base && !model?.is_locked && isDefault && (
              <Tooltip
                title={
                  isDisabled
                    ? 'This section is not available for guest user'
                    : undefined
                }
              >
                <Button
                  disabled={isDisabled || isRunningModelOutputServer}
                  variant="outlined_red"
                  onClick={() => handleOpenDatasetDialog()}
                >
                  Edit
                </Button>
              </Tooltip>
            )}
          </div>
          <div className="w-[30%]">
            <FormInput disabled name={'label'} />
          </div>
        </div>
        <Dialog
          maxWidth="sm"
          fullWidth
          open={openSelectDatasetDialog}
          onClose={handleCloseDatasetDialog}
        >
          <DialogTitle style={{ cursor: 'move' }}>
            <h2 className="font-bold">Select Dataset</h2>
          </DialogTitle>
          <DialogContent>
            <div className="w-full">
              {!!datasets && (
                <FormSelect
                  name="value"
                  options={datasets.map((dataset) => ({
                    label: dataset.name,
                    value: dataset.id,
                  }))}
                />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex gap-3 px-4 pb-3">
              <Button
                autoFocus
                variant="outlined"
                type="button"
                onClick={handleCloseDatasetDialog}
                tabIndex={3}
              >
                Cancel
              </Button>
              <Button
                autoFocus
                loading={isUpdatingModel}
                onClick={handleOnSubmit}
                tabIndex={3}
                variant="contained_blue"
              >
                Save
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
};

export default DatasetConfiguration;
